import React, { useState } from 'react'
import { TextField, Banner, Button, IconButton, ConfirmDialog, SegmentedToggle } from '@ui'
import { useFlags } from '@flags/useFlags'
import { useSsoSettings } from '../hooks/useSsoSettings.js'

const VERIFY_TXT_PREFIX = 'soundcheck-verify='
const VERIFY_TXT_LABEL = '_soundcheck-verify'

export function SsoSettingsPage() {
  const flags = useFlags()
  const enabled = !!flags?.['entra-sso']?.enabled

  const {
    config,
    loading,
    updateConfig,
    setEnforcement,
    addDomain,
    verifyDomain,
    removeDomain,
    disableSso,
  } = useSsoSettings()

  const [tenantId, setTenantId] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [metadataUrl, setMetadataUrl] = useState('')
  const [saveError, setSaveError] = useState(null)
  const [saving, setSaving] = useState(false)

  const [newDomain, setNewDomain] = useState('')
  const [domainError, setDomainError] = useState(null)

  const [enforceConfirm, setEnforceConfirm] = useState(false)
  const [disableConfirm, setDisableConfirm] = useState(false)

  React.useEffect(() => {
    if (!config) return
    setTenantId(config.tenantId || '')
    setClientId(config.clientId || '')
    setMetadataUrl(config.metadataUrl || '')
  }, [config?.tenantId, config?.clientId, config?.metadataUrl])

  if (!enabled) {
    return (
      <div style={styles.wrap}>
        <h2 style={styles.title}>SSO</h2>
        <Banner tone="info">
          SSO is not enabled for this organization. Ask your Soundcheck contact to enable
          the Entra SSO feature if you'd like to evaluate it.
        </Banner>
      </div>
    )
  }

  if (loading) return <div style={styles.loading}>Loading…</div>

  const verifiedDomains = (config?.domains || []).filter((d) => d.verified)
  const canEnforce = verifiedDomains.length > 0 && !!config?.tenantId

  async function handleSave(e) {
    e.preventDefault()
    setSaveError(null)
    setSaving(true)
    try {
      const input = { tenantId, clientId }
      if (metadataUrl) input.metadataUrl = metadataUrl
      if (clientSecret) input.clientSecret = clientSecret
      await updateConfig(input)
      setClientSecret('')
    } catch (err) {
      setSaveError(err?.message || 'Failed to save SSO configuration.')
    } finally {
      setSaving(false)
    }
  }

  async function handleAddDomain(e) {
    e.preventDefault()
    setDomainError(null)
    const normalized = newDomain.trim().toLowerCase()
    if (!normalized || !normalized.includes('.')) {
      setDomainError('Enter a valid domain, e.g. company.com')
      return
    }
    try {
      await addDomain(normalized)
      setNewDomain('')
    } catch (err) {
      setDomainError(err?.message || 'Failed to claim domain.')
    }
  }

  async function handleEnforcementChange(next) {
    if (next === (config?.enforcement || 'disabled')) return
    if (next === 'enforced') {
      if (!canEnforce) return
      setEnforceConfirm(true)
      return
    }
    await setEnforcement(next)
  }

  async function handleConfirmEnforced() {
    setEnforceConfirm(false)
    await setEnforcement('enforced')
  }

  async function handleConfirmDisable() {
    setDisableConfirm(false)
    await disableSso()
  }

  return (
    <div style={styles.wrap}>
      <h2 style={styles.title}>SSO</h2>

      <section style={styles.section}>
        <div style={styles.sectionTitle}>Entra configuration</div>
        <div style={styles.domainHint}>
          Register an app in Entra, then paste the tenant ID and client ID here. Users
          whose email domain has been verified below will be able to sign in via Entra.
        </div>
        {saveError && <Banner tone="error">{saveError}</Banner>}
        <form onSubmit={handleSave} style={styles.formGrid}>
          <label style={styles.label}>
            <span style={styles.fieldLabel}>Tenant ID</span>
            <TextField
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="contoso.onmicrosoft.com"
              required
              aria-label="Entra tenant ID"
            />
          </label>
          <label style={styles.label}>
            <span style={styles.fieldLabel}>Client (application) ID</span>
            <TextField
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              required
              aria-label="Entra client ID"
            />
          </label>
          <label style={styles.label}>
            <span style={styles.fieldLabel}>
              Client secret
              {config?.clientSecretMasked && (
                <span style={styles.secretStatus}> (currently {config.clientSecretMasked})</span>
              )}
            </span>
            <TextField
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={config?.clientSecretMasked ? 'Leave blank to keep existing' : 'Paste client secret'}
              aria-label="Entra client secret"
            />
          </label>
          <label style={styles.label}>
            <span style={styles.fieldLabel}>OpenID metadata URL (optional)</span>
            <TextField
              type="text"
              value={metadataUrl}
              onChange={(e) => setMetadataUrl(e.target.value)}
              placeholder="https://login.microsoftonline.com/<tenant>/v2.0/.well-known/openid-configuration"
              aria-label="Entra metadata URL"
            />
          </label>
          <div style={styles.formFooter}>
            <Button type="submit" loading={saving} loadingLabel="Saving…">
              Save configuration
            </Button>
          </div>
        </form>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionTitle}>Verified domains</div>
        <div style={styles.domainHint}>
          Only users with an email in a verified domain will be routed to Entra.
          Verification adds a DNS TXT record at{' '}
          <code>{VERIFY_TXT_LABEL}.&lt;domain&gt;</code>.
        </div>
        {(config?.domains || []).length > 0 && (
          <ul style={styles.domainList}>
            {config.domains.map((d) => (
              <li key={d.domain} style={styles.domainRow}>
                <div style={styles.domainName}>
                  {d.domain}
                  {d.verified
                    ? <span style={styles.verified}> ✓ Verified</span>
                    : <span style={styles.unverified}> Pending verification</span>}
                </div>
                {!d.verified && (
                  <div style={styles.domainHint}>
                    Add TXT record: <code>{VERIFY_TXT_LABEL}.{d.domain}</code>{' '}
                    = <code>{VERIFY_TXT_PREFIX}{d.verificationToken}</code>
                  </div>
                )}
                <div style={styles.domainActions}>
                  {!d.verified && (
                    <Button
                      size="sm"
                      onClick={() => verifyDomain(d.domain)}
                      aria-label={`Verify ${d.domain}`}
                    >
                      Verify
                    </Button>
                  )}
                  <IconButton
                    ariaLabel={`Remove ${d.domain}`}
                    onClick={() => removeDomain(d.domain)}
                  >
                    ×
                  </IconButton>
                </div>
              </li>
            ))}
          </ul>
        )}
        {domainError && <Banner tone="error">{domainError}</Banner>}
        <form onSubmit={handleAddDomain} style={styles.addForm}>
          <div style={{ flex: 1 }}>
            <TextField
              type="text"
              placeholder="company.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              aria-label="Add SSO domain"
            />
          </div>
          <Button type="submit">+ Claim domain</Button>
        </form>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionTitle}>Enforcement</div>
        <div style={styles.domainHint}>
          When enforced, password sign-in is disabled for members of this organization.
          Passkeys and TOTP continue to work as fallbacks, so at least one admin should
          have a passkey enrolled before enforcing. Enforcement requires a verified
          domain.
        </div>
        <SegmentedToggle
          ariaLabel="SSO enforcement"
          value={config?.enforcement || 'disabled'}
          onChange={handleEnforcementChange}
          options={[
            { value: 'disabled', label: 'Disabled' },
            { value: 'optional', label: 'Optional' },
            { value: 'enforced', label: 'Enforced', disabled: !canEnforce },
          ]}
        />
        {!canEnforce && (
          <div style={styles.domainHint}>
            Verify a domain before enforcing SSO.
          </div>
        )}
      </section>

      {config?.status !== 'disabled' && (config?.tenantId || config?.clientId) && (
        <section style={styles.section}>
          <div style={styles.sectionTitle}>Danger zone</div>
          <div style={styles.domainHint}>
            Disabling SSO clears the stored configuration and wipes the client secret.
            Domains remain claimed — remove them individually if you want to release them.
          </div>
          <Button variant="danger" onClick={() => setDisableConfirm(true)}>
            Disable SSO
          </Button>
        </section>
      )}

      <ConfirmDialog
        open={enforceConfirm}
        tone="danger"
        title="Enforce SSO for all members?"
        message="Members of this organization will no longer be able to sign in with a password. Passkeys and TOTP will still work. Make sure at least one admin has a passkey enrolled before continuing."
        confirmLabel="Enforce SSO"
        onConfirm={handleConfirmEnforced}
        onCancel={() => setEnforceConfirm(false)}
      />

      <ConfirmDialog
        open={disableConfirm}
        tone="danger"
        title="Disable Entra SSO?"
        message="The stored configuration and client secret will be removed. Members will fall back to password/passkey/TOTP sign-in."
        confirmLabel="Disable SSO"
        onConfirm={handleConfirmDisable}
        onCancel={() => setDisableConfirm(false)}
      />
    </div>
  )
}

const styles = {
  wrap: { maxWidth: 720, margin: '0 auto' },
  title: { fontSize: 20, fontWeight: 600, marginBottom: 24 },
  loading: { fontSize: 13, color: 'var(--text-secondary)', padding: 32 },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 16 },
  sectionTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 },
  domainHint: { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 },
  fieldLabel: { fontSize: 12, fontWeight: 500, marginBottom: 4, display: 'block' },
  secretStatus: { fontWeight: 400, color: 'var(--text-secondary)' },
  formGrid: { display: 'flex', flexDirection: 'column', gap: 14 },
  label: { display: 'block' },
  formFooter: { display: 'flex', justifyContent: 'flex-end', marginTop: 4 },
  addForm: { display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' },
  domainList: { listStyle: 'none', padding: 0, margin: '0 0 12px 0' },
  domainRow: { padding: '10px 0', borderBottom: '0.5px solid var(--border)' },
  domainName: { fontSize: 13, fontWeight: 500 },
  domainActions: { display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' },
  verified: { fontSize: 11, color: 'var(--success, #0a8a3f)', marginLeft: 8 },
  unverified: { fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 },
}
